/**
 * Fuente única para humanizar duraciones de ejecución de IA (latencia).
 * Reemplaza el cálculo `(ms / 1000).toFixed(1) + 's'` duplicado e inline
 * en SectionDraftDialog / ProposeStructureDialog / GenerateAllDialog.
 *
 * Unidades por `t()` (namespace `common`) — nunca hardcodeadas en JSX.
 * Ver OT-0003 (hivanexus-infra/Docs/ordenes-de-trabajo/feat/OT-0003-humanizar-duraciones.md).
 */

import type { TFunction } from 'i18next'

const MS_PER_SECOND = 1000
const MS_PER_MINUTE = 60_000
const SECONDS_PER_MINUTE = 60
const SECONDS_PER_HOUR = 3600

export function formatDuration(ms: number, t: TFunction): string {
  const safeMs = Math.max(0, ms)

  if (safeMs < MS_PER_SECOND) {
    return t('duration.ms', { ns: 'common', value: Math.round(safeMs) })
  }

  if (safeMs < MS_PER_MINUTE) {
    // 1 decimal; si el redondeo llega a 60.0 (ej. 59.95s), cae al branch
    // de min+s de abajo en lugar de imprimir "60.0 s".
    const seconds = Math.round(safeMs / 100) / 10
    if (seconds < 60) {
      return t('duration.s', { ns: 'common', value: seconds.toFixed(1) })
    }
  }

  // Segundos enteros totales — misma base para min+s y h+min, así el
  // redondeo en cascada (ej. 3599999ms → 3600s) siempre elige el branch
  // correcto sin duplicar la lógica de redondeo.
  const totalSeconds = Math.round(safeMs / MS_PER_SECOND)

  if (totalSeconds < SECONDS_PER_HOUR) {
    const minutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE)
    const seconds = totalSeconds % SECONDS_PER_MINUTE
    return t('duration.min_s', { ns: 'common', min: minutes, s: seconds })
  }

  const hours = Math.floor(totalSeconds / SECONDS_PER_HOUR)
  const minutes = Math.floor(
    (totalSeconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE,
  )
  return t('duration.h_min', { ns: 'common', h: hours, min: minutes })
}
